import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, LogOut, Save, RotateCcw, Copy, Download, ArrowLeft, CalendarClock } from "lucide-react";
import { useContent, DEFAULT_CONTENT } from "../content/siteContent";
import useDaysUntil from "../hooks/useDaysUntil";

// Light gate. Override at build time with REACT_APP_EDIT_KEY. Because this is a
// static site, the key only guards localStorage edits on this device — it is not
// server-side auth. Keep the /editsite path private.
const EDIT_KEY = process.env.REACT_APP_EDIT_KEY || "sajilo-admin-2026";
const UNLOCK_KEY = "sh_edit_ok";

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.16em] font-semibold text-textsoft">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11.5px] text-textsoft/70">{hint}</span>}
    </label>
  );
}

const inputCls =
  "mt-2 w-full px-4 py-3 rounded-xl border border-line bg-white text-ink placeholder:text-textsoft/50 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-soft focus:border-blue";

function Gate({ onUnlock }) {
  const [val, setVal] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (val === EDIT_KEY) {
      try {
        sessionStorage.setItem(UNLOCK_KEY, "1");
      } catch (err) {
        /* ignore */
      }
      onUnlock();
    } else {
      toast.error("Wrong key");
    }
  };
  return (
    <div className="min-h-screen bg-paper flex items-center justify-center px-5">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-white border border-line p-8 shadow-[0_30px_70px_-34px_rgba(15,42,71,0.3)]">
        <span className="inline-flex w-11 h-11 items-center justify-center rounded-xl bg-blue-soft text-blue">
          <Lock size={20} strokeWidth={1.8} />
        </span>
        <h1 className="mt-5 font-display text-[22px] font-semibold text-ink tracking-tight">Site editor</h1>
        <p className="mt-1.5 text-[13.5px] text-textsoft">Enter the editor key to continue.</p>
        <input
          type="password"
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="Editor key"
          className={inputCls}
          data-testid="editsite-key"
        />
        <button type="submit" className="btn btn-primary w-full mt-4">Unlock</button>
        <Link to="/" className="mt-4 inline-flex items-center gap-1.5 text-[12.5px] text-textsoft hover:text-ink">
          <ArrowLeft size={13} /> Back to site
        </Link>
      </form>
    </div>
  );
}

export default function EditSite() {
  const { content, updateContent, resetContent } = useContent();
  const [ok, setOk] = useState(false);

  useEffect(() => {
    try {
      setOk(sessionStorage.getItem(UNLOCK_KEY) === "1");
    } catch (e) {
      /* ignore */
    }
  }, []);

  // Local form state mirrors content; hydrate when content changes / on unlock.
  const [form, setForm] = useState(content);
  useEffect(() => {
    setForm(content);
  }, [content, ok]);

  const days = useDaysUntil(form.betaDate);

  const editable = useMemo(
    () => ({
      betaDate: form.betaDate,
      contactEmail: form.contactEmail,
      facebookUrl: form.facebookUrl,
      linkedinUrl: form.linkedinUrl,
      traction: form.traction,
    }),
    [form]
  );

  if (!ok) return <Gate onUnlock={() => setOk(true)} />;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setTraction = (patch) => setForm((f) => ({ ...f, traction: { ...f.traction, ...patch } }));

  const save = () => {
    updateContent(editable);
    toast.success("Saved — live on this browser", {
      description: "Publish to everyone: Export → replace public/site-content.json → redeploy.",
    });
  };

  const json = JSON.stringify(editable, null, 2);

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(json);
      toast.success("Copied site-content.json");
    } catch (e) {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const downloadJson = () => {
    const blob = new Blob([json + "\n"], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "site-content.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const logout = () => {
    try {
      sessionStorage.removeItem(UNLOCK_KEY);
    } catch (e) {
      /* ignore */
    }
    setOk(false);
  };

  return (
    <div className="min-h-screen bg-paper text-ink">
      <header className="border-b border-line bg-paper/85 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-[860px] mx-auto px-5 h-16 flex items-center justify-between">
          <div className="inline-flex items-baseline gap-2">
            <span className="font-display text-[18px] font-semibold tracking-tight text-ink">
              Sajilo<span className="text-blue">Health</span>
            </span>
            <span className="text-[12px] text-textsoft font-mono">/ editor</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="inline-flex items-center gap-1.5 text-[13px] text-textsoft hover:text-ink">
              <ArrowLeft size={14} /> Site
            </Link>
            <button onClick={logout} className="inline-flex items-center gap-1.5 text-[13px] text-textsoft hover:text-ink">
              <LogOut size={14} /> Lock
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[860px] mx-auto px-5 py-10">
        <div className="flex items-center gap-2.5 rounded-xl border border-blue/20 bg-blue-soft/60 px-4 py-3">
          <CalendarClock size={16} className="text-blue" />
          <span className="text-[13.5px] text-ink">
            Private beta in <strong>{days ?? "—"}</strong> days ({form.betaDate}).
          </span>
        </div>

        {/* Beta + contact */}
        <section className="mt-8 rounded-2xl bg-white border border-line p-6 sm:p-7">
          <h2 className="font-display text-[17px] font-semibold text-ink tracking-tight">Launch &amp; contact</h2>
          <div className="mt-5 grid sm:grid-cols-2 gap-5">
            <Field label="Beta release date" hint="Drives the days-to-beta countdown.">
              <input type="date" value={form.betaDate || ""} onChange={(e) => set({ betaDate: e.target.value })} className={inputCls} data-testid="edit-betaDate" />
            </Field>
            <Field label="Contact email">
              <input type="email" value={form.contactEmail || ""} onChange={(e) => set({ contactEmail: e.target.value })} className={inputCls} data-testid="edit-email" />
            </Field>
            <Field label="Facebook URL">
              <input type="url" value={form.facebookUrl || ""} onChange={(e) => set({ facebookUrl: e.target.value })} className={inputCls} placeholder="https://facebook.com/…" data-testid="edit-facebook" />
            </Field>
            <Field label="LinkedIn URL" hint="Shown in the footer when set.">
              <input type="url" value={form.linkedinUrl || ""} onChange={(e) => set({ linkedinUrl: e.target.value })} className={inputCls} placeholder="https://linkedin.com/company/…" data-testid="edit-linkedin" />
            </Field>
          </div>
        </section>

        {/* Traction */}
        <section className="mt-6 rounded-2xl bg-white border border-line p-6 sm:p-7">
          <h2 className="font-display text-[17px] font-semibold text-ink tracking-tight">Traction numbers</h2>
          <p className="mt-1 text-[13px] text-textsoft">Shown on the investor card. Use any text (e.g. “120+”).</p>
          <div className="mt-5 grid sm:grid-cols-3 gap-5">
            <Field label="Clinics & hospitals">
              <input value={form.traction?.clinics || ""} onChange={(e) => setTraction({ clinics: e.target.value })} className={inputCls} data-testid="edit-clinics" />
            </Field>
            <Field label="Patients registered">
              <input value={form.traction?.patients || ""} onChange={(e) => setTraction({ patients: e.target.value })} className={inputCls} data-testid="edit-patients" />
            </Field>
            <Field label="Transactions">
              <input value={form.traction?.transactions || ""} onChange={(e) => setTraction({ transactions: e.target.value })} className={inputCls} data-testid="edit-transactions" />
            </Field>
          </div>
        </section>

        {/* Actions */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button onClick={save} className="btn btn-primary" data-testid="edit-save">
            <Save size={15} /> Save (live here)
          </button>
          <button onClick={copyJson} className="btn btn-ghost">
            <Copy size={15} /> Copy JSON
          </button>
          <button onClick={downloadJson} className="btn btn-ghost">
            <Download size={15} /> Download
          </button>
          <button
            onClick={() => {
              resetContent();
              toast.success("Reset to defaults");
            }}
            className="btn btn-ghost"
          >
            <RotateCcw size={15} /> Reset
          </button>
        </div>

        {/* Publish */}
        <section className="mt-8 rounded-2xl border border-line bg-paper-sunken/60 p-6">
          <h2 className="font-display text-[15px] font-semibold text-ink tracking-tight">Publish to everyone</h2>
          <ol className="mt-3 space-y-1.5 text-[13.5px] text-textsoft list-decimal pl-5">
            <li><strong>Save</strong> applies changes live on this device (and any open site tab) instantly.</li>
            <li>To publish for all visitors: <strong>Copy JSON</strong> and replace <code className="font-mono text-[12px]">frontend/public/site-content.json</code>, then redeploy.</li>
          </ol>
          <pre className="mt-4 overflow-x-auto rounded-xl bg-ink text-white/90 p-4 text-[12px] font-mono leading-relaxed">{json}</pre>
        </section>
      </main>
    </div>
  );
}
